import { useState, FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLD from "@/components/base/JsonLD";
import { kontaktSchema } from "@/components/base/PageSchemas";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function KontaktPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const hp = formData.get("phone_alt");
    if (hp && typeof hp === "string" && hp.trim() !== "") {
      setStatus("success");
      form.reset();
      return;
    }

    formData.delete("phone_alt");

    setStatus("submitting");
    setErrorMsg("");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9if95uc26n1c7c5pt3g", {
        method: "POST",
        body: formData,
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const isSuccess =
        response.ok && parsed && parsed.code === "OK";

      if (isSuccess) {
        setStatus("success");
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          "Wystąpił błąd podczas wysyłania. Spróbuj ponownie.";
        setStatus("error");
        setErrorMsg(serverMsg);
      }
    } catch {
      setStatus("error");
      setErrorMsg("Nie udało się wysłać wiadomości. Sprawdź połączenie i spróbuj ponownie.");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLD data={kontaktSchema} />
      <Navbar />
      <main>
        <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-background-50">
          <div className="mx-auto max-w-[1440px] px-4 text-center md:px-8 lg:px-16 xl:px-20">
            <Breadcrumb />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent-500 mb-3 block">Kontakt</span>
            <h1 className="mx-auto max-w-3xl font-heading text-3xl text-foreground-950 md:text-4xl lg:text-5xl">
              Skontaktuj się ze mną
            </h1>
          </div>
        </section>

        <section className="pb-16 md:pb-24">
          <div className="mx-auto max-w-[1440px] px-4 md:px-8 lg:px-16 xl:px-20">
            <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-20">
              {/* Contact info */}
              <div>
                <p className="text-base text-foreground-600 leading-relaxed mb-8">
                  Masz pytania dotyczące kredytów? Chcesz umówić bezpłatną konsultację? Skontaktuj się ze mną – odpowiem na każde zapytanie i pomogę rozwiać wątpliwości.
                </p>

                <div className="flex flex-col gap-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 shrink-0">
                      <i className="ri-map-pin-line text-accent-500 text-xl" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 uppercase tracking-wide mb-1">Adres biura</p>
                      <p className="text-base font-semibold text-foreground-800">38-500 Sanok, ul. Dworcowa 11A</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 shrink-0">
                      <i className="ri-phone-line text-accent-500 text-xl" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 uppercase tracking-wide mb-1">Telefon</p>
                      <a href="tel:698084044" className="text-base font-semibold text-foreground-800 hover:text-primary-500 transition-colors">698 084 044</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 shrink-0">
                      <i className="ri-mail-line text-accent-500 text-xl" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 uppercase tracking-wide mb-1">E-mail</p>
                      <a href="mailto:biuro@kredytpodkarpacie.pl" className="text-base font-semibold text-foreground-800 hover:text-primary-500 transition-colors">biuro@kredytpodkarpacie.pl</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 shrink-0">
                      <i className="ri-global-line text-accent-500 text-xl" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 uppercase tracking-wide mb-1">Strona</p>
                      <p className="text-base font-semibold text-foreground-800">kredytpodkarpacie.pl</p>
                    </div>
                  </div>
                </div>

                {/* Google Map */}
                <div className="w-full h-[200px] md:h-[240px] rounded-2xl overflow-hidden bg-background-200 border border-background-200">
                  <iframe
                    title="EXPERT KREDYTOWY – ul. Dworcowa 11A, Sanok"
                    src="https://maps.google.com/maps?q=ul.+Dworcowa+11A,+38-500+Sanok,+Polska&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>

              {/* Form */}
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-background-200/60 shadow-sm">
                <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
                  Bezpłatna konsultacja
                </h2>
                <p className="text-sm text-foreground-500 mb-6">
                  Wypełnij formularz – skontaktuję się z Tobą w ciągu 24 godzin.
                </p>

                {status === "success" && (
                  <div className="mb-6 p-4 bg-accent-100 rounded-xl">
                    <div className="flex items-center gap-2 mb-1">
                      <i className="ri-check-line text-accent-500" />
                      <span className="text-sm font-semibold text-accent-700">Wiadomość wysłana!</span>
                    </div>
                    <p className="text-sm text-accent-600">Dziękuję za kontakt. Odpowiem najszybciej jak to możliwe.</p>
                  </div>
                )}

                {status === "error" && errorMsg && (
                  <div className="mb-6 p-4 bg-red-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-1">
                      <i className="ri-error-warning-line text-red-500" />
                      <span className="text-sm font-semibold text-red-700">Błąd wysyłania</span>
                    </div>
                    <p className="text-sm text-red-600">{errorMsg}</p>
                  </div>
                )}

                <form
                  data-readdy-form
                  id="d9if95uc26n1c7c5pt3g"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium text-foreground-500 uppercase tracking-wide mb-1.5">
                      Imię i nazwisko
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors"
                      placeholder="Twoje imię i nazwisko"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-foreground-500 uppercase tracking-wide mb-1.5">
                        E-mail
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors"
                        placeholder="twoj@email.pl"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-xs font-medium text-foreground-500 uppercase tracking-wide mb-1.5">
                        Telefon
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors"
                        placeholder="698 000 000"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-500 uppercase tracking-wide mb-1.5">
                      Wiadomość
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors resize-none"
                      placeholder="Opisz krótko, czego dotyczy Twoje zapytanie..."
                    />
                    <p className="text-xs text-foreground-400 mt-1">Maksymalnie 500 znaków</p>
                  </div>

                  {/* Honeypot */}
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-alt"
                  />

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="w-full px-6 py-3.5 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {status === "submitting" ? "Wysyłanie..." : "Wyślij wiadomość"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
